import React from 'react';
import { Moment } from 'moment';
import { useMutation } from '@apollo/client';
import {
  MutationRemoveCardFromWorkerArgs,
  Success,
  AppErrorCode,
  useReactivateWorkerCardMutation,
  useGetWorkerCardQuery,
} from 'apollo/generated/client-operations';
import { REMOVE_CARD_FROM_WORKER } from 'containers/worker/helpers/queries';
import { useIsMounted } from 'utils';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { CheckIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { AlertService } from 'components/alertService';
import {
  WorkerCard,
  ReassignBadgeState,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/worker/modals/badgeModal/components/types';
import {
  getFormDefaultValues,
  getFormInputs,
} from './ReactivateBadgeModalContent.forms';
import { ReactivateWorkerBadgeData } from './types';

export interface ReactivateBadgeModalContentProps {
  setOpen: () => void;
  onAction: (reassignBadgeState?: ReassignBadgeState) => void;
  workerCardIdToReactivate: string;
  workerCards: WorkerCard[];
  expirationDate?: Moment;
}

export function ReactivateBadgeModalContent(
  props: ReactivateBadgeModalContentProps,
): React.ReactElement {
  const {
    setOpen,
    onAction,
    workerCardIdToReactivate,
    workerCards,
    expirationDate,
  } = props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const { data: workerCardData } = useGetWorkerCardQuery({
    fetchPolicy: 'network-only',
    variables: {
      workerCardId: workerCardIdToReactivate,
    },
    skip: !workerCardIdToReactivate,
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
  });

  const workerCard = workerCardData?.getWorkerCard;

  const [reactivateWorkerCard] = useReactivateWorkerCardMutation();

  const [removeCardFromWorker] = useMutation<
    Success,
    MutationRemoveCardFromWorkerArgs
  >(REMOVE_CARD_FROM_WORKER, {
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      setIsFetching(false);
    },
  });

  const onSubmit: FormOnSubmit<ReactivateWorkerBadgeData> = async (
    data,
    event,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    const reactivateCardAndRemoveExisting = async (options?: {
      forceCreate?: boolean;
    }): Promise<void> => {
      await reactivateWorkerCard({
        variables: {
          id: workerCardIdToReactivate,
          forceCreate: options?.forceCreate,
          endDate: workerCard?.isTemporary
            ? data.expirationDate.endOf('day').toDate()
            : undefined,
        },
      });

      const workerCardIdsToDeactivate = data.badges
        ?.filter(({ selected }) => selected)
        .map(({ workerCard: wc }) => wc.workerCardId);

      if (workerCardIdsToDeactivate?.length) {
        await removeCardFromWorker({
          variables: {
            workerCardIds: workerCardIdsToDeactivate,
          },
        });
      }
    };

    try {
      await reactivateCardAndRemoveExisting();
      AlertService.alert('success', 'Success', 'Badge reactivated');
      onAction();
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { workerCardInfo } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (workerCardInfo) {
          onAction({
            workerCardInfo,
            reassignBadge: async (): Promise<void> => {
              await reactivateCardAndRemoveExisting({ forceCreate: true });
            },
          });
        } else {
          AlertService.alert(
            'danger',
            'Something went wrong!',
            workerCardAlreadyExistsError.message,
          );
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const formInputs = React.useMemo(
    () => getFormInputs({ workerCard }),
    [workerCard],
  );
  const defaultValues = React.useMemo(() => {
    return getFormDefaultValues({ workerCard, workerCards, expirationDate });
  }, [
    // remove workerCards from dependency so that `defaultValues` won't get recalculated
    // when `workerCards` are re-fetched after adding/removing cards
    workerCard,
    expirationDate?.toISOString(),
  ]);

  return (
    <ModalFormContent
      setOpen={setOpen}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Reactivate badge"
      actionIcon={CheckIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
